import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CalendarIcon, 
  ClockIcon, 
  WrenchScrewdriverIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Appointment, getAppointments, cancelAppointment, formatAppointmentDate, getAppointmentStatusText, getAppointmentStatusColor, getActiveAppointment } from '@/services/appointments';

const AppointmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'scheduled' | 'completed' | 'canceled'>('all');
  const [hasActiveAppointment, setHasActiveAppointment] = useState(false);

  useEffect(() => {
    loadAppointments();
    checkActiveAppointment();
  }, []);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getAppointments();
      setAppointments(data);
    } catch (err) {
      console.error('Error loading appointments:', err);
      setError('Error al cargar las citas');
    } finally {
      setLoading(false);
    }
  };

  const checkActiveAppointment = async () => {
    try {
      const activeData = await getActiveAppointment();
      setHasActiveAppointment(activeData.hasActiveAppointment);
    } catch (err) {
      console.error('Error checking active appointment:', err);
      setHasActiveAppointment(false);
    }
  };

  const handleCancelAppointment = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que quieres cancelar esta cita?')) {
      return;
    }

    try {
      await cancelAppointment(id);
      await loadAppointments(); // Recargar la lista
      await checkActiveAppointment(); // Verificar si aún hay cita activa
    } catch (err) {
      console.error('Error canceling appointment:', err);
      alert('Error al cancelar la cita');
    }
  };

  const handleBookNewAppointment = () => {
    if (hasActiveAppointment) {
      // Si tiene cita activa, ir directamente a modificarla
      navigate('/book-appointment');
    } else {
      // Si no tiene cita activa, ir a seleccionar servicio
      navigate('/services');
    }
  };

  const filteredAppointments = appointments.filter(appointment => {
    if (filter === 'all') return true;
    return appointment.status === filter;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <CalendarIcon className="w-5 h-5" />;
      case 'completed':
        return <WrenchScrewdriverIcon className="w-5 h-5" />;
      case 'canceled':
        return <ExclamationTriangleIcon className="w-5 h-5" />;
      default:
        return <ClockIcon className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Cargando citas...
          </h3>
          <p className="text-gray-600">
            Por favor espera mientras cargamos tus citas
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <CalendarIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Mis Citas
          </h1>
          <p className="text-gray-600 mb-6">
            Gestiona tus citas y servicios programados
          </p>
          
          <button
            onClick={handleBookNewAppointment}
            className="btn-primary flex items-center mx-auto"
          >
            {hasActiveAppointment ? (
              <>
                <PencilIcon className="w-4 h-4 mr-2" />
                Modificar Cita Activa
              </>
            ) : (
              <>
                <PlusIcon className="w-4 h-4 mr-2" />
                Reservar Nueva Cita
              </>
            )}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Todas ({appointments.length})
            </button>
            <button
              onClick={() => setFilter('scheduled')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === 'scheduled'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Programadas ({appointments.filter(a => a.status === 'scheduled').length})
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === 'completed'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Completadas ({appointments.filter(a => a.status === 'completed').length})
            </button>
            <button
              onClick={() => setFilter('canceled')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === 'canceled'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Canceladas ({appointments.filter(a => a.status === 'canceled').length})
            </button>
          </div>
        </div>

        {/* Lista de citas */}
        <div className="space-y-4">
          {filteredAppointments.length === 0 ? (
            <div className="text-center py-12">
              <CalendarIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {filter === 'all' ? 'No tienes citas' : `No hay citas ${getAppointmentStatusText(filter).toLowerCase()}`}
              </h3>
              <p className="text-gray-600 mb-6">
                {filter === 'all' 
                  ? 'Reserva tu primera cita para comenzar'
                  : 'No se encontraron citas con este estado'
                }
              </p>
              {filter === 'all' && (
                <button
                  onClick={handleBookNewAppointment}
                  className="btn-primary"
                >
                  {hasActiveAppointment ? 'Modificar Cita Activa' : 'Reservar Primera Cita'}
                </button>
              )}
            </div>
          ) : (
            filteredAppointments.map((appointment) => (
              <div
                key={appointment.id}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Header de la cita */}
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        {getStatusIcon(appointment.status)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {appointment.services.length === 1
                            ? appointment.services[0].name
                            : `${appointment.services.length} servicios seleccionados`}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {formatAppointmentDate(appointment.appointmentDate)}
                        </p>
                      </div>
                    </div>

                    {/* Detalles de los servicios */}
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Servicios:</p>
                      <div className="space-y-2">
                        {appointment.services.map((service, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center space-x-3">
                              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                                <WrenchScrewdriverIcon className="w-4 h-4 text-blue-600" />
                              </div>
                              <span className="text-sm text-gray-900">{service.name}</span>
                            </div>
                            <div className="flex items-center space-x-3 text-sm text-gray-600">
                              <span>${service.price}</span>
                              <span>•</span>
                              <span>{service.duration} min</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">Total:</span>
                          <div className="text-right">
                            <div className="font-semibold text-gray-900">${appointment.totalPrice}</div>
                            <div className="text-sm text-gray-600">{appointment.totalDuration} min</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Categorías y estado */}
                    <div className="flex items-center space-x-2 mb-3 flex-wrap gap-2">
                      {/* Mostrar categorías únicas */}
                      {Array.from(new Set(appointment.services.map(s => s.category.name))).map((categoryName, index) => {
                        const category = appointment.services.find(s => s.category.name === categoryName)?.category;
                        return (
                          <span
                            key={index}
                            className="px-2 py-1 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: `${category?.color}20`,
                              color: category?.color
                            }}
                          >
                            {categoryName}
                          </span>
                        );
                      })}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAppointmentStatusColor(appointment.status)}`}>
                        {getAppointmentStatusText(appointment.status)}
                      </span>
                    </div>

                    {/* Notas */}
                    {appointment.notes && (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700 mb-1">Notas</p>
                        <p className="text-sm text-gray-600 bg-gray-50 rounded p-2">
                          {appointment.notes}
                        </p>
                      </div>
                    )}

                    {/* Mecánico asignado */}
                    {appointment.mechanic && (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700">Mecánico asignado</p>
                        <p className="text-sm text-gray-600">{appointment.mechanic}</p>
                      </div>
                    )}
                  </div>

                  {/* Acciones */}
                  <div className="flex flex-col space-y-2 ml-4">
                    {appointment.status === 'scheduled' && (
                      <>
                        <button
                          onClick={() => {
                            navigate('/book-appointment');
                          }}
                          className="flex items-center space-x-1 px-3 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <PencilIcon className="w-4 h-4" />
                          <span>Editar</span>
                        </button>
                        <button
                          onClick={() => handleCancelAppointment(appointment.id)}
                          className="flex items-center space-x-1 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" />
                          <span>Cancelar</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentsPage;